import { Column } from "Components/AppGrid/shipeezi-new-grid";
import { MemberTimesheet } from "Models/members/member.timesheet";
import { Task } from "Models/task";
import { useTranslation } from "react-i18next";

export const useTimesheet = () => {
  const { t: translate } = useTranslation();

  interface GetWorkedAndBreakHoursProps {
    timesheetList: MemberTimesheet[];
  }
  const getWorkedAndBreakHours = ({ timesheetList }: GetWorkedAndBreakHoursProps) => {
    const workedAndBreakHours = {
      worked: {
        hours: 0,
        minutes: 0,
      },
      break: {
        hours: 0,
        minutes: 0,
      },
    };

    if (timesheetList?.length) {
      timesheetList.forEach((timesheet) => {
        if (timesheet.breakFrom && timesheet.backFromBreak) {
          const breaksTime = Math.abs(
            new Date(timesheet.backFromBreak).getTime() - new Date(timesheet.breakFrom).getTime()
          );
          const breaksTimeMinutes = Math.floor(breaksTime / 60000);
          const breaksTimeHours = Math.floor(breaksTimeMinutes / 60);

          workedAndBreakHours.break.hours += breaksTimeHours;
          workedAndBreakHours.break.minutes += breaksTimeMinutes % 60;
        }

        if (timesheet.arrivedAtTheResidence && timesheet.leavingTheResidence) {
          const workedTime = Math.abs(
            new Date(timesheet.leavingTheResidence).getTime() -
              new Date(timesheet.arrivedAtTheResidence).getTime()
          );

          const workedTimeMinutes = Math.floor(
            workedTime / 60000 - workedAndBreakHours.break.minutes
          );
          const workedTimeHours = Math.floor(
            workedTimeMinutes / 60 - workedAndBreakHours.break.hours
          );

          workedAndBreakHours.worked.hours += workedTimeHours;
          workedAndBreakHours.worked.minutes += workedTimeMinutes % 60;
        }
      });
    }

    return workedAndBreakHours;
  };

  const timesheetGridColumns: Column[] = [
    {
      name: "task",
      searchIdentifier: "reference",
      hideSort: true,
      title: translate("order"),
      formatComponent: (value: Task) => {
        if (value) {
          return value?.job?.reference;
        }

        return "-";
      },
    },
    {
      name: "taskType",
      title: translate("task"),
      hideSearch: true,
      formatComponent: (value: string, row: MemberTimesheet) => {
        if (row?.taskType) {
          return row?.taskType;
        }

        if (value) {
          return value;
        }

        if (row?.task?.type) return row?.task?.type;

        return "-";
      },
    },
    {
      name: "task",
      title: translate("team"),
      searchIdentifier: "teamName",
      formatComponent: (value: Task) => {
        if (value && value?.team && value?.team?.name) {
          return value?.team?.name;
        }

        return "-";
      },
    },
    {
      name: "workedTime",
      hideSearch: true,
      title: translate("workedTime"),
      formatComponent: (_value, row: MemberTimesheet) => {
        const workedAndBreakHours = getWorkedAndBreakHours({ timesheetList: [row] });

        if (row.arrivedAtTheResidence && row.leavingTheResidence) {
          return `${workedAndBreakHours.worked?.hours}h ${
            workedAndBreakHours.worked.minutes % 60
          }m`;
        }

        return "-";
      },
    },
    {
      name: "breaks",
      hideSearch: true,
      title: translate("breaks"),
      formatComponent: (_value, row: MemberTimesheet) => {
        if (row.breakFrom && row.backFromBreak) {
          const workedAndBreakHours = getWorkedAndBreakHours({ timesheetList: [row] });

          return `${workedAndBreakHours.break.hours}h ${workedAndBreakHours.break.minutes % 60}m`;
        }

        return "-";
      },
    },
    {
      name: "totalTime",
      hideSearch: true,
      title: translate("totalTime"),
      formatComponent: (_value, row: MemberTimesheet) => {
        if (row.arrivedAtTheResidence && row.leavingTheResidence) {
          const workedAndBreakHours = getWorkedAndBreakHours({ timesheetList: [row] });
          const totalTimeHours = workedAndBreakHours.worked.hours + workedAndBreakHours.break.hours;
          const totalTimeMinutes =
            workedAndBreakHours.worked.minutes + workedAndBreakHours.break.minutes;

          return `${totalTimeHours}h ${totalTimeMinutes % 60}m`;
        }

        return "-";
      },
    },
  ];

  return { timesheetGridColumns, getWorkedAndBreakHours };
};
